import { useState, useEffect, useRef } from "react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Play, Pause, Volume2, VolumeX, SkipBack, SkipForward, Video, Headphones } from "lucide-react";
import SEO from "@/components/SEO";

const EPISODES = [
  {
    id: "ep1",
    title: "NeuroNest: A Thoughtful Neurodiversity App",
    description:
      "An introduction to NeuroNest — what it is, who it's for, and why we built it. We talk about sensory-safe design, the four app modes, community places, and our mission to create tools that respect how your brain works.",
    src: `${process.env.PUBLIC_URL}/videos/podcast-ep1.mp4`,
    host: "Sally",
  },
];

function formatTime(seconds) {
  if (!seconds || isNaN(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function MediaPlayer({ episode, mode }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    setPlaying(false);
    setCurrentTime(0);
    if (ref.current) {
      ref.current.load();
    }
  }, [episode.id, mode]);

  const togglePlay = () => {
    if (!ref.current) return;
    if (playing) {
      ref.current.pause();
    } else {
      ref.current.play();
    }
    setPlaying(!playing);
  };

  const seek = (e) => {
    if (!ref.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    ref.current.currentTime = pct * duration;
  };

  const skip = (seconds) => {
    if (!ref.current) return;
    ref.current.currentTime = Math.max(0, Math.min(duration, ref.current.currentTime + seconds));
  };

  return (
    <div data-testid="media-player" className="w-full">
      {mode === "video" ? (
        <div className="rounded-2xl overflow-hidden bg-[#1A1D24] mb-6 aspect-video">
          <video
            ref={ref}
            src={episode.src}
            className="w-full h-full object-contain block"
            onTimeUpdate={() => setCurrentTime(ref.current?.currentTime || 0)}
            onLoadedMetadata={() => setDuration(ref.current?.duration || 0)}
            onEnded={() => setPlaying(false)}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            muted={muted}
            playsInline
            controls={false}
            onClick={togglePlay}
          />
        </div>
      ) : (
        <div className="flex items-center justify-center mb-6">
          <div className="w-48 h-48 rounded-2xl overflow-hidden shadow-[0_8px_30px_rgba(47,58,86,0.2)]">
            <img
              src={`${process.env.PUBLIC_URL}/screenshots/sally.webp`}
              alt="Sally — NeuroNest Podcast host"
              className="w-full h-full object-cover"
            />
          </div>
          <audio
            ref={ref}
            src={episode.src}
            onTimeUpdate={() => setCurrentTime(ref.current?.currentTime || 0)}
            onLoadedMetadata={() => setDuration(ref.current?.duration || 0)}
            onEnded={() => setPlaying(false)}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            muted={muted}
          />
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-center gap-6 mb-6">
        <button
          data-testid="skip-back"
          onClick={() => skip(-15)}
          className="text-[#5A6376] hover:text-brand-teal transition-colors"
          aria-label="Skip back 15 seconds"
        >
          <SkipBack size={20} strokeWidth={1.5} />
        </button>
        <button
          data-testid="play-pause"
          onClick={togglePlay}
          className="w-14 h-14 rounded-full bg-brand-teal flex items-center justify-center text-white shadow-lg shadow-brand-teal/20 hover:bg-brand-teal/90 transition-all duration-300 hover:-translate-y-0.5"
        >
          {playing ? <Pause size={22} /> : <Play size={22} className="ml-1" />}
        </button>
        <button
          data-testid="skip-forward"
          onClick={() => skip(15)}
          className="text-[#5A6376] hover:text-brand-teal transition-colors"
          aria-label="Skip forward 15 seconds"
        >
          <SkipForward size={20} strokeWidth={1.5} />
        </button>
      </div>

      {/* Progress bar */}
      <div
        data-testid="progress-bar"
        className="w-full h-2 bg-[#E7E9EC] rounded-full cursor-pointer mb-3 group"
        onClick={seek}
      >
        <div
          className="h-full bg-brand-teal rounded-full relative transition-all duration-100"
          style={{ width: duration ? `${(currentTime / duration) * 100}%` : "0%" }}
        >
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-brand-teal border-2 border-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>

      {/* Time + Volume */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#8B95A5] font-mono">
          {formatTime(currentTime)}
        </span>
        <button
          data-testid="mute-toggle"
          onClick={() => setMuted(!muted)}
          className="text-[#8B95A5] hover:text-brand-teal transition-colors"
          aria-label={muted ? "Unmute" : "Mute"}
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
        <span className="text-xs text-[#8B95A5] font-mono">
          {formatTime(duration)}
        </span>
      </div>
    </div>
  );
}

export default function PodcastPage() {
  const [activeEpisode, setActiveEpisode] = useState(EPISODES[0]);
  const [mode, setMode] = useState("video");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="podcast-page" className="min-h-screen bg-[#F8F9FB]">
      <SEO
        title="The NeuroNest Podcast"
        description="Watch and listen to conversations about neurodiversity, sensory-safe design, and building tools that respect how your brain works. Hosted by Sally."
        path="/podcast"
        type="article"
      />
      <Navbar />

      <main className="pt-28 pb-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-brand-purple font-semibold mb-4">
              Watch & Listen
            </p>
            <h1
              data-testid="podcast-title"
              className="font-heading text-3xl md:text-5xl font-bold text-[#2F3A56] leading-tight mb-4"
            >
              The NeuroNest Podcast
            </h1>
            <p className="text-base md:text-lg text-[#5A6376] max-w-xl mx-auto leading-relaxed">
              Conversations about neurodiversity, sensory-safe design, and
              building tools that respect how your brain works.
            </p>
          </div>

          {/* Player Card */}
          <div className="bg-white rounded-3xl border border-[#E7E9EC] shadow-[0_4px_20px_rgba(0,0,0,0.04)] p-8 md:p-10 mb-10">
            {/* Mode toggle + Episode info */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div>
                <span className="inline-block text-[10px] uppercase tracking-widest text-brand-teal font-semibold mb-1">
                  Now Playing
                </span>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-[#2F3A56]">
                  {activeEpisode.title}
                </h2>
                <p className="text-xs text-[#8B95A5] mt-1">
                  Hosted by {activeEpisode.host}
                </p>
              </div>

              {/* Video / Audio toggle */}
              <div className="flex items-center gap-1 bg-[#F0F1F3] rounded-full p-1 flex-shrink-0">
                <button
                  data-testid="toggle-video-mode"
                  onClick={() => setMode("video")}
                  className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium transition-all duration-300 ${
                    mode === "video"
                      ? "bg-brand-teal text-white shadow-sm"
                      : "text-[#5A6376] hover:text-brand-teal"
                  }`}
                >
                  <Video size={13} /> Video
                </button>
                <button
                  data-testid="toggle-audio-mode"
                  onClick={() => setMode("audio")}
                  className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium transition-all duration-300 ${
                    mode === "audio"
                      ? "bg-brand-teal text-white shadow-sm"
                      : "text-[#5A6376] hover:text-brand-teal"
                  }`}
                >
                  <Headphones size={13} /> Audio
                </button>
              </div>
            </div>

            <p className="text-sm text-[#5A6376] leading-relaxed mb-6">
              {activeEpisode.description}
            </p>

            <MediaPlayer episode={activeEpisode} mode={mode} />
          </div>

          {/* Episode List */}
          <div>
            <h3 className="font-heading text-sm uppercase tracking-widest text-[#8B95A5] font-semibold mb-4">
              All Episodes
            </h3>
            <div className="space-y-3">
              {EPISODES.map((ep) => {
                const isActive = activeEpisode.id === ep.id;
                return (
                  <button
                    key={ep.id}
                    data-testid={`episode-card-${ep.id}`}
                    onClick={() => setActiveEpisode(ep)}
                    className={`w-full text-left flex items-center gap-4 p-5 rounded-2xl border transition-all duration-300 ${
                      isActive
                        ? "bg-brand-teal/5 border-brand-teal/30 shadow-[0_4px_20px_rgba(110,198,193,0.12)]"
                        : "bg-white border-[#E7E9EC] hover:border-brand-teal/20 hover:shadow-[0_4px_20px_rgba(0,0,0,0.04)]"
                    }`}
                  >
                    <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
                      <img
                        src={`${process.env.PUBLIC_URL}/screenshots/sally.webp`}
                        alt="Sally"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold ${isActive ? "text-brand-teal" : "text-[#2F3A56]"}`}>
                        {ep.title}
                      </p>
                      <p className="text-xs text-[#8B95A5] mt-0.5">
                        Hosted by {ep.host}
                      </p>
                    </div>
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isActive ? "bg-brand-teal" : "bg-[#F0F1F3]"
                      }`}
                    >
                      <Play
                        size={14}
                        className={isActive ? "text-white ml-0.5" : "text-[#8B95A5] ml-0.5"}
                        strokeWidth={1.5}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-8 p-5 bg-white rounded-2xl border border-[#E7E9EC] text-center">
            <p className="text-xs text-[#8B95A5]">
              More episodes coming soon. Follow us for updates.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
